<template>
        <section class="page book-overview-page">
                <ReaderSiteHeader />

                <component :is="overviewComponent" v-if="isKnownBook && overview && totalPages"
                        :has-reading-progress="savedPageNumber !== null" :resume-context="resumeContext" :current-page-number="savedPageNumber"
                        :annotations-error="annotationsError" :is-loading-annotations="isLoadingAnnotations"
                        :bookmark-count="bookmarks.length" :note-count="notes.length" @open-reader="openReader" />
                <div v-else-if="loadError || overviewLoadError" role="alert">{{ loadError || overviewLoadError }}</div>
                <div v-else class="loading" role="status" aria-label="Загрузка сведений об учебнике"></div>
        </section>
</template>

<script setup lang="ts">
        import ReaderSiteHeader from "@/window/ReaderSiteHeader.vue"
        import { onBeforeUnmount, onMounted, ref, type Component } from "vue"
        import { storeToRefs } from "pinia"
        import { useRoute, useRouter } from "vue-router"
        import { getReaderPdfBookManifest } from "@/ts/reader-pdf-book-manifest"
        import { pStore } from "@/ts/storage/store"
        import { getSourcePageLocation } from "@/ts/pdf-page-map"
        import { browserStorageGateway } from "@/ts/storage/create-json-storage-gateway"
        import { readReaderPdfReadingProgress } from "@/ts/reader-pdf-progress-storage"
        import { useReaderAnnotations } from "@/ts/use-reader-annotations"

        const { overviewComponent } = defineProps<{overviewComponent: Component}>()

        const route = useRoute()
        const router = useRouter()
        const requestedBookManifest = getReaderPdfBookManifest(route.meta.bookId ?? route.params.bookId)
        const isKnownBook = Boolean(requestedBookManifest && requestedBookManifest.overviewPath === route.path)
        const pdfBook = pStore()

        if (isKnownBook && requestedBookManifest) pdfBook.selectBook(requestedBookManifest)

        const { bookManifest, overview, overviewLoadError, overviewPageCount: totalPages } = storeToRefs(pdfBook)
        const savedPageNumber = ref<number | null>(null)
        const resumeContext = ref<string | null>(null)
        const { bookmarks, notes, error: annotationsError, isLoading: isLoadingAnnotations } = useReaderAnnotations({ bookId: bookManifest.value.id, pdfId: bookManifest.value.pdf.id })
        const loadError = ref("")
        let isOverviewMounted = false

        const openReader = async () => {
                if (!isKnownBook) return

                await router.push(`${bookManifest.value.readPath}/${savedPageNumber.value ?? bookManifest.value.firstPageNumber}`)
        }

        onMounted(async () => {
                if (!isKnownBook) {
                        loadError.value = "Учебник не найден."
                        return
                }

                isOverviewMounted = true
                const manifest = bookManifest.value

                try {
                        const loaded = await pdfBook.loadOverview()

                        if (!loaded || !isOverviewMounted) return

                        const savedProgress = readReaderPdfReadingProgress(browserStorageGateway, {
                                bookId: manifest.id,
                                pdfId: manifest.pdf.id,
                        })

                        savedPageNumber.value = savedProgress?.pageNumber ?? null
                        if (!savedProgress) return

                        const savedLocation = getSourcePageLocation(savedProgress.pageNumber)

                        if (!savedLocation) return

                        const savedPosition = savedLocation.sourcePageNumber * 2 + (savedLocation.side === "right" ? 1 : 0)
                        const tocItems = pdfBook.sourceTocItems.length > 0 ? pdfBook.sourceTocItems : manifest.fallbackToc ?? []
                        const chapter = [...tocItems].reverse().find((item) => item.pageNumber * 2 + (item.pageSide === "right" ? 1 : 0) <= savedPosition)
                        const section = chapter?.children
                                .slice()
                                .reverse()
                                .find((item) => item.pageNumber * 2 + (item.pageSide === "right" ? 1 : 0) <= savedPosition)

                        if (chapter && section) {
                                resumeContext.value = `${chapter.title.replace(/\.\s.*$/u, "")} · ${section.title.replace(/^§(\d+)/u, "§ $1")}`
                        }
                } catch (error) {
                        loadError.value = error instanceof Error ? error.message : "Не удалось загрузить сведения об учебнике."
                }
        })

        onBeforeUnmount(() => { isOverviewMounted = false })
</script>
